// Adapted from Microsoft Elixir-Framework (MIT), v3.0.1164, aa6c10ab1882f8fa20c1da308e6c1fc6fe2fe5e9.
// Source: packages/fx/src/components/Editor/RichText/plugins/commandbar/ElxRichTextCommandBarPlugin.ts. Local adaptations: Bug 38755929.
import { CompatiblePluginEventType as PluginEventType, getFormatState, Browser, toggleBold, toggleItalic, toggleUnderline, } from 'roosterjs';
export class ElxRichTextCommandBarPlugin {
    /**
     * Construct a new instance of RibbonPlugin object
     * @param delayUpdateTime The time to wait before refresh the button when user do some editing operation in editor
     */
    constructor(delayUpdateTime = 200) {
        this.delayUpdateTime = delayUpdateTime;
        this.timer = 0;
    }
    getName() {
        return 'ElxRichTextCommandBarPlugin';
    }
    setReadOnly(readOnly) {
        this.readOnly = readOnly;
    }
    setDisabled(disabled) {
        this.disabled = disabled;
    }
    setUIUtilities(uiUtilities) {
        this.uiUtilities = uiUtilities;
    }
    initialize(editor) {
        this.editor = editor;
    }
    dispose() {
        var _a, _b;
        if (this.timer) {
            (_b = (_a = this.editor) === null || _a === void 0 ? void 0 : _a.getDocument().defaultView) === null || _b === void 0 ? void 0 : _b.clearTimeout(this.timer);
            this.timer = 0;
        }
        this.editor = undefined;
        this.formatState = undefined;
    }
    /**
     * Handle events triggered from editor
     * @param event PluginEvent object
     */
    onPluginEvent(event) {
        if (this.disabled) {
            return;
        }
        switch (event.eventType) {
            case PluginEventType.EditorReady:
            case PluginEventType.ContentChanged:
            case PluginEventType.ZoomChanged:
                this.updateFormat();
                break;
            case PluginEventType.KeyDown:
                if (!this.readOnly && !event.handledByEditFeature) {
                    this.handleFormattingShortcut(event.rawEvent);
                }
                if (!this.readOnly) {
                    this.delayUpdate();
                }
                break;
            case PluginEventType.Input:
            case PluginEventType.MouseDown:
                if (!this.readOnly) {
                    this.delayUpdate();
                }
                break;
        }
    }
    handleFormattingShortcut(event) {
        const modifier = Browser.isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
        if (!this.editor || !modifier || event.altKey || event.shiftKey || event.defaultPrevented ||
            event.isComposing || event.keyCode === 229 || event.getModifierState('AltGraph')) {
            return;
        }
        const key = event.key.toLowerCase();
        if (key !== 'b' && key !== 'i' && key !== 'u') {
            return;
        }
        // Native shortcuts do not populate Rooster's collapsed-selection format cache.
        event.preventDefault();
        if (!event.repeat) {
            const toggle = { b: toggleBold, i: toggleItalic, u: toggleUnderline }[key];
            toggle(this.editor);
        }
    }
    /**
     * Register a callback to be invoked when format state of editor is changed, returns a disposer function.
     */
    registerFormatChangedCallback(callback) {
        this.onFormatChanged = callback;
        return () => {
            this.onFormatChanged = undefined;
        };
    }
    /**
     * When user clicks on a button, call this method to let the plugin to handle this click event
     * @param button The button that is clicked
     * @param key Key of child menu item that is clicked if any
     * @param strings The localized string map for this button
     */
    onButtonClick(button, key, strings) {
        var _a;
        if (this.editor && !this.disabled && !this.readOnly && this.uiUtilities) {
            this.editor.stopShadowEdit();
            button.onClick(this.editor, key, strings, this.uiUtilities);
            if (button.isChecked || button.isDisabled || ((_a = button.dropDownMenu) === null || _a === void 0 ? void 0 : _a.getSelectedItemKey)) {
                this.updateFormat();
            }
        }
    }
    /**
     * Enter live preview state (shadow edit) of editor if there is a non-collapsed selection
     * @param button The button that triggered this action
     * @param key Key of the hovered button sub item
     * @param strings The localized string map for this button
     */
    startLivePreview(button, key, strings) {
        if (this.editor && this.uiUtilities) {
            const isInShadowEdit = this.editor.isInShadowEdit();
            // If editor is already in shadow edit, no need to check again.
            // And the check result may be incorrect because the content is changed from last shadow edit and the cached selection path won't apply
            const range = !isInShadowEdit && this.editor.getSelectionRangeEx();
            if (isInShadowEdit || (range && !range.areAllCollapsed)) {
                this.editor.startShadowEdit();
                button.onClick(this.editor, key, strings, this.uiUtilities);
            }
        }
    }
    /**
     * Leave live preview state (shadow edit) of editor
     */
    stopLivePreview() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.stopShadowEdit();
    }
    delayUpdate() {
        var _a;
        const window = (_a = this.editor) === null || _a === void 0 ? void 0 : _a.getDocument().defaultView;
        if (!window) {
            return;
        }
        if (this.timer) {
            window.clearTimeout(this.timer);
        }
        this.timer = window.setTimeout(() => {
            var _a;
            this.timer = 0;
            (_a = this.updateFormat) === null || _a === void 0 ? void 0 : _a.call(this);
        }, this.delayUpdateTime);
    }
    updateFormat() {
        if (this.editor && this.onFormatChanged) {
            const newFormatState = getFormatState(this.editor);
            if (!this.formatState ||
                Object.keys(newFormatState).some((key) => this.formatState && newFormatState[key] != this.formatState[key])) {
                this.formatState = newFormatState;
                this.onFormatChanged(newFormatState);
            }
        }
    }
}
